import React from 'react';
import styles from './ProfileIconButton.module.scss';
import { ApplicationResolution } from '../../applicationResolution';
import { isProfileIconImage } from '../../typeGuards';
import { logger } from '../../../common/logger';
import { getAssetUrl } from '../../utils/cloudinary';
import { cls } from '../../View/utils';
import { ProfileIcon as ProfileIconType } from '../../../common/types';
import { profileColorAltTexts } from '../../constants';
import { getTranslation } from '../../localizations/translations';
import { Language } from '@yleisradio/areena-types';
import { useAppFeaturesContext } from '../../contexts/AppFeaturesContext/AppFeaturesContext';

const fallBackButton: ProfileIconType = {
  type: 'color',
  value: 'rgba(167,167,167)',
};

export const constructInitialsAltText = (
  languageCode: Language,
  text: string,
  icon: ProfileIconType
): string => {
  const colorAltText = profileColorAltTexts[icon.value]?.[languageCode] ?? '';

  const trimmedAltText = [
    getTranslation('editProfileInitials_initials', languageCode),
    text.trim(),
    colorAltText.trim(),
  ]
    .filter(string => string !== '')
    .join(' ');

  return trimmedAltText;
};

type Props = {
  icon: ProfileIconType;
  navigationId?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  text?: string;
  imageAlt?: string;
  textSize?: 'three-xl' | 'four-xl' | 's';
  ['data-testid']?: string;
  isEditProfileButton?: boolean;
};

/**
 * Wrap this component inside another element, which decides the icon dimensions and shape
 */
export const ProfileIconButton: React.FC<Props> = ({
  icon = fallBackButton,
  text,
  imageAlt,
  navigationId,
  onClick,
  textSize = 'three-xl',
  ['data-testid']: dataTestId,
  isEditProfileButton = false,
}) => {
  const { applicationResolution, languageCode } = useAppFeaturesContext();

  const textButtonAriaLabel = isEditProfileButton
    ? constructInitialsAltText(languageCode, text || '', icon)
    : '';

  const isImage = isProfileIconImage(icon);
  const imageSrc = isImage
    ? getAssetUrl(
        { id: icon.value, version: '' },
        {
          format: 'jpg',
          width:
            applicationResolution === ApplicationResolution.FULLHD ? 280 : 187,
          height:
            applicationResolution === ApplicationResolution.FULLHD ? 280 : 187,
        }
      )
    : undefined;

  if (!imageAlt && isImage) {
    logger.warn('Image profile-icon is missing alt-text');
  }

  if (!isImage && !text) {
    logger.warn('Color profile-icon is missing text');
  }

  return (
    <button
      className={cls({
        [styles.root]: true,
        [styles.threeXL]: textSize === 'three-xl',
        [styles.fourXL]: textSize === 'four-xl',
        [styles.s]: textSize === 's',
      })}
      id={navigationId}
      onClick={onClick}
      style={!isImage ? { backgroundColor: icon.value } : {}}
      data-testid={dataTestId}
      aria-label={textButtonAriaLabel}
    >
      {isImage ? <img src={imageSrc} alt={imageAlt} /> : text}
    </button>
  );
};
